/**
 * Hymnal reference — the studio site's display logic over `@gjl/music-manager/
 * hymnal-pages`. See `docs/pm/active/fugue-lab-hymnal-seo.md` (P2).
 *
 * Structure lifted from the Great Host of Composers catalog (`clients/
 * demo-great-host-composers/src/lib/catalog.ts`), which solves the same problem in
 * production at ~400 generated routes. Two disciplines taken from it verbatim:
 *
 * 1. EVERY HELPER IS PURE and takes the data as an argument, so the same code runs
 *    in SSR, in tests, and in the browser. GHC's note — "never a compiled-in
 *    `import works.json`" — is the rule; nothing in this module imports the dataset.
 *    Whoever renders supplies it.
 *
 * 2. NEVER EMIT A DEAD END. GHC's `ReadingsLink` renders nothing rather than a
 *    broken link. The equivalents here (`relatedByMeter`, `otherByPerson`) return
 *    empty arrays rather than placeholder rows, so a template can decide not to
 *    render the section at all.
 *
 * ⚠️ DELIVERY IS AN OPEN DECISION, NOT SOLVED HERE. `hymnal_1982.json` is 469KB, and
 * studio's pages are statically imported (App.tsx: renderToString is synchronous, so
 * React.lazy cannot be used). A naive `import hymnal from '…json'` would therefore
 * put 469KB into the marketing site's main chunk and every visitor to the homepage
 * would pay for it. This module stays data-injected precisely so that decision can
 * be made independently of the display logic. See the plan, §5.2a.
 */
import { personDisplayToSlugBase } from '@gjl/music-manager/hymnal-pages'

import { sortLetterFor, sortNameFor } from './sort-name'
import type {
  HymnPage,
  HymnRef,
  HymnalPages,
  MeterPage,
  PersonPage,
  PersonRole,
  SectionPage,
  TunePage,
} from '@gjl/music-manager/hymnal-pages'

/** O(1) slug lookups for the detail routes. Build once per render, not per page. */
export interface HymnalIndex {
  pages: HymnalPages
  hymnBySlug: Map<string, HymnPage>
  hymnByNumber: Map<string, HymnPage>
  tuneBySlug: Map<string, TunePage>
  personBySlug: Map<string, PersonPage>
  meterBySlug: Map<string, MeterPage>
  sectionBySlug: Map<string, SectionPage>
  /**
   * Every spelling a person is credited under → their page.
   *
   * A hymn stores the RAW credit string ("Ralph Vaughan Williams, 1872-1958 (alt.)"),
   * not a person slug, so going from a hymn to a person page means matching that
   * string. Doing it with `people.find(p => p.variants.includes(name))` is O(people)
   * per credit and was about to be written twice; this makes it O(1) and keeps the
   * two call sites from drifting on how a credit resolves.
   */
  personByVariant: Map<string, PersonPage>
}

export function indexHymnalPages(pages: HymnalPages): HymnalIndex {
  return {
    pages,
    hymnBySlug: new Map(pages.hymns.map((h) => [h.slug, h])),
    hymnByNumber: new Map(pages.hymns.map((h) => [h.number, h])),
    tuneBySlug: new Map(pages.tunes.map((t) => [t.slug, t])),
    personBySlug: new Map(pages.people.map((p) => [p.slug, p])),
    meterBySlug: new Map(pages.meters.map((m) => [m.slug, m])),
    sectionBySlug: new Map(pages.sections.map((s) => [s.slug, s])),
    personByVariant: new Map(pages.people.flatMap((p) => p.variants.map((v) => [v, p] as const))),
  }
}

/** A facet value with its hymn count — drives browse filters and their labels. */
export interface Facet {
  value: string
  slug?: string
  count: number
}

const byCountThenValue = (a: Facet, b: Facet) => b.count - a.count || a.value.localeCompare(b.value)

function facetsOf(hymns: readonly HymnPage[], pick: (h: HymnPage) => { value?: string; slug?: string }): Facet[] {
  const acc = new Map<string, Facet>()
  for (const h of hymns) {
    const { value, slug } = pick(h)
    if (!value) continue
    const key = slug ?? value
    const existing = acc.get(key)
    if (existing) existing.count += 1
    else acc.set(key, { value, slug, count: 1 })
  }
  return [...acc.values()].sort(byCountThenValue)
}

export const meterFacets = (hymns: readonly HymnPage[]): Facet[] =>
  facetsOf(hymns, (h) => ({ value: h.meter, slug: h.meterSlug }))

export const languageFacets = (hymns: readonly HymnPage[]): Facet[] => facetsOf(hymns, (h) => ({ value: h.language }))

export const keyFacets = (hymns: readonly HymnPage[]): Facet[] => facetsOf(hymns, (h) => ({ value: h.key }))

/**
 * Sections in BOOK ORDER, not by count — unlike every other facet here.
 *
 * The others are incidental axes where "most hymns first" is the useful ranking.
 * The sections are a printed sequence a reader already knows; sorting Advent
 * after Praise to God because Praise to God is bigger would misrepresent the book
 * and make the spine unscannable.
 */
export const sectionFacets = (pages: HymnalPages): Facet[] =>
  pages.sections.map((s) => ({ value: s.label, slug: s.slug, count: s.hymns.length }))

const norm = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

/** Language is 714/720 English, so a six-way facet would be six rows of noise
 *  where five have single digits. Two buckets is the honest shape. */
export const LANGUAGE_FILTERS = ['English', 'Other'] as const
export type LanguageFilter = (typeof LANGUAGE_FILTERS)[number]

/**
 * Every narrowing the browse page can apply. Mirrors the query string exactly —
 * this is never an independent store, so a view is always a link (D13).
 *
 * TWO FACETS WERE REMOVED 2026-08-09, both because they answered a question
 * nothing was asking:
 *
 * - **Letter.** A–Z over first lines duplicated the search box, which already
 *   matches a first line from its opening words and does it better (it does not
 *   force you to know whether "O come, O come" files under O). The alphabetical
 *   view still exists where it belongs — `/hymns`, one crawlable page.
 * - **Credit.** It filtered to hymns that HAVE an author/composer/arranger at
 *   all, which is 560 of 720 and answers nobody's question. Finding a PERSON was
 *   always the real intent, and `searchHymns` already matches all five credit
 *   fields, so typing "Vaughan Williams" was the working path the whole time.
 *
 * A stale `?letter=A` or `?role=composer` link degrades correctly rather than
 * breaking: unknown params are dropped, not applied, so it shows everything.
 */
export interface Filters {
  section?: string
  meter?: string
  key?: string
  language?: LanguageFilter
}

/**
 * Apply every active filter. Absent keys narrow nothing, so `{}` is the identity —
 * which is what lets the caller derive `Filters` straight from the query string
 * without special-casing "no filters".
 */
export function applyFilters(hymns: readonly HymnPage[], filters: Filters): HymnPage[] {
  return hymns.filter(
    (h) =>
      (!filters.section || h.sectionSlug === filters.section) &&
      (!filters.meter || h.meterSlug === filters.meter) &&
      (!filters.key || h.key === filters.key) &&
      (!filters.language || (filters.language === 'English' ? h.language === 'English' : h.language !== 'English'))
  )
}

/** True when anything is narrowing. Drives the browse-vs-results switch. */
export const anyFilterActive = (filters: Filters): boolean => Object.values(filters).some(Boolean)

/**
 * Free-text search across the fields a musician actually searches by.
 *
 * ⚠️ THIS IS NOW THE ONLY IMPLEMENTATION. There used to be a second one
 * (`lib/search-index.ts`) scanning a separately fetched flat file, and the two
 * ranked differently — the same query could order results one way on the index
 * page and another anywhere else. The flat file was also a second copy of a corpus
 * every route in this build already compiles in, so it bought a 24KB download and
 * a round-trip to duplicate data the browser had already parsed.
 *
 * Ranking, in the order a musician expects:
 *   0  the exact hymn number — typing "272" means hymn 272, not every hymn whose
 *      credits happen to contain "272"
 *   1  first line starts with the query
 *   2  tune name matches exactly (tune names are shouted caps and searched whole)
 *   3  a credited person's name starts with the query
 *   4  anything else that merely contains it
 * Ties break on hymn order, so a result set always reads in book order.
 */
export function searchHymns(hymns: readonly HymnPage[], query: string): HymnPage[] {
  const q = norm(query)
  if (!q) return []
  const scored: { h: HymnPage; score: number }[] = []
  for (const h of hymns) {
    const first = norm(h.firstLine)
    const tune = norm(h.tuneName ?? '')
    const people = [h.author, h.translator, h.composer, h.arranger, h.harmonizer]
      .filter(Boolean)
      .map((v) => norm(String(v)))
    const haystack = [norm(h.number), first, tune, ...people]
    if (!haystack.some((v) => v.includes(q))) continue
    const score =
      h.number === query.trim()
        ? 0
        : first.startsWith(q)
          ? 1
          : tune === q
            ? 2
            : people.some((p) => p.startsWith(q))
              ? 3
              : 4
    scored.push({ h, score })
  }
  return scored.sort((a, b) => a.score - b.score || a.h.order - b.h.order).map((s) => s.h)
}

/**
 * Move the typeahead highlight by one step, cycling through the input itself.
 *
 * `-1` IS A REAL POSITION — the text input — not a null. So the cycle is
 * −1, 0, 1, … n−1 and back to −1, which gives two behaviours a keyboard user
 * expects and a naive modulo does not: ↑ from the input lands on the LAST
 * suggestion in one press, and ↓ off the end returns you to your text rather
 * than pinning the highlight to the bottom of the list.
 *
 * Extracted and tested because the first version was written inline as
 * `(i + delta + n + 1) % (n + 1)` — unreadable, and wrong.
 */
export function nextSuggestionIndex(current: number, delta: 1 | -1, count: number): number {
  if (count <= 0) return -1
  const next = current + delta
  if (next < -1) return count - 1
  if (next >= count) return -1
  return next
}

/** One typeahead row — a place to go, never a bare string. */
export interface Suggestion {
  kind: 'hymn' | 'tune' | 'person' | 'section'
  label: string
  detail?: string
  href: string
}

/**
 * Typeahead across hymns, tunes, people and sections.
 *
 * NEVER SUGGESTS A DEAD END — every row is built from an entity that has a page,
 * so following one always lands somewhere with content. That is the same rule
 * `relatedByMeter` and `otherByPerson` follow by returning [] rather than
 * placeholder rows.
 */
export function suggest(index: HymnalIndex, query: string, limit = 8): Suggestion[] {
  const q = norm(query)
  if (q.length < 2) return []
  const base = `/hymnal/${index.pages.source.id}`
  const out: Suggestion[] = []

  for (const s of index.pages.sections) {
    if (norm(s.label).includes(q)) {
      out.push({
        kind: 'section',
        label: s.label,
        detail: `${s.hymns.length} hymns`,
        href: `${base}/section/${s.slug}`,
      })
    }
  }
  for (const t of index.pages.tunes) {
    if (norm(t.name).startsWith(q)) {
      out.push({ kind: 'tune', label: t.name, detail: t.meter, href: `${base}/tune/${t.slug}` })
    }
  }
  for (const p of index.pages.people) {
    if (norm(p.name).includes(q)) {
      out.push({ kind: 'person', label: p.name, detail: `${p.hymns.length} hymns`, href: `${base}/person/${p.slug}` })
    }
  }
  for (const h of searchHymns(index.pages.hymns, query).slice(0, limit)) {
    out.push({ kind: 'hymn', label: h.firstLine, detail: `Hymn ${h.number}`, href: `${base}/hymn/${h.slug}` })
  }
  // Sections and tunes first — they are destinations that answer a whole query
  // ("Advent"), where a hymn row answers only one line of it.
  const rank: Record<Suggestion['kind'], number> = { section: 0, tune: 1, person: 2, hymn: 3 }
  return out.sort((a, b) => rank[a.kind] - rank[b.kind]).slice(0, limit)
}

/**
 * Metrical substitution — other tunes in the same meter, which is the query a
 * musician actually has ("this text is CM; what else can we sing it to?").
 *
 * Excludes the hymn's own tune. Returns [] when the meter has no page, so a template
 * can omit the section rather than render an empty one.
 */
export function relatedByMeter(index: HymnalIndex, hymn: HymnPage, limit = 12): TunePage[] {
  if (!hymn.meterSlug) return []
  const meter = index.meterBySlug.get(hymn.meterSlug)
  if (!meter) return []
  const seen = new Set<string>()
  const out: TunePage[] = []
  for (const ref of meter.hymns) {
    const other = index.hymnBySlug.get(ref.slug)
    const tuneSlug = other?.tuneSlug
    if (!tuneSlug || tuneSlug === hymn.tuneSlug || seen.has(tuneSlug)) continue
    const tune = index.tuneBySlug.get(tuneSlug)
    if (!tune) continue
    seen.add(tuneSlug)
    out.push(tune)
    if (out.length >= limit) break
  }
  return out
}

/**
 * The name a hymnal credit files under.
 *
 * ⚠️ THE STRIP IS LOAD-BEARING, NOT TIDYING. `PersonPage.name` is the full credit
 * as printed — "John Mason Neale, 1818-1866" — because the dates are what
 * disambiguate two musicians who share a name. `sortNameFor` was ported from a
 * catalogue whose names carry no dates, so it takes the last token and files 560
 * of the 607 people in this corpus under "1818-1866". Measured, not theorised:
 * the test below is what caught it.
 *
 * `personDisplayToSlugBase` already strips dates and editorial qualifiers, and is
 * the same function the model uses to derive person slugs — so the name a page
 * files under and the name its URL is built from can never disagree.
 */
export const personSortName = (credit: string): string => sortNameFor(personDisplayToSlugBase(credit))
export const personSortLetter = (credit: string): string => sortLetterFor(personDisplayToSlugBase(credit))

/**
 * Comparator over file-under names, full credit as the tiebreak.
 *
 * Use this, never `compareBySortName` directly — that one sees the raw credit,
 * dates and all. Shipping the wrong one put 47 people under `1`, `3`, `5`, `(`
 * and friends on `/people` while the unit test for `personSortName` passed,
 * because the test exercised the helper and the page called something else.
 */
export const comparePersonByFileName = (a: string, b: string): number =>
  personSortName(a).localeCompare(personSortName(b), 'en', { sensitivity: 'base' }) || a.localeCompare(b)

/** A person credited across a set of hymns, with how many of them they appear on. */
export interface CreditCount {
  slug: string
  name: string
  count: number
}

/**
 * Everyone credited across a set of hymns, keyed by person slug.
 *
 * Resolves the raw credit string through `personByVariant`, which means the two
 * spellings of one person collapse into one row — the whole point of the model's
 * variant merging, and something a naive count over credit strings gets wrong.
 *
 * Only people who HAVE a page are returned, so a caller may link every row
 * without guarding. That is safe today because `personMinHymns` is 1; it stays
 * correct if the threshold ever rises, because the lookup simply yields fewer
 * rows rather than rows pointing at nothing.
 */
export function creditedAcross(index: HymnalIndex, hymns: readonly HymnPage[]): Map<string, CreditCount> {
  const out = new Map<string, CreditCount>()
  for (const h of hymns) {
    for (const credit of [h.author, h.translator, h.composer, h.arranger, h.harmonizer]) {
      if (!credit) continue
      const person = index.personByVariant.get(credit)
      if (!person) continue
      const row = out.get(person.slug) ?? { slug: person.slug, name: person.name, count: 0 }
      row.count += 1
      out.set(person.slug, row)
    }
  }
  return out
}

/** Every person credited on a hymn, in reading order, skipping absent credits. */
export function creditsFor(hymn: HymnPage): { role: PersonRole; name: string }[] {
  const rows: { role: PersonRole; name?: string }[] = [
    { role: 'author', name: hymn.author },
    { role: 'translator', name: hymn.translator },
    { role: 'composer', name: hymn.composer },
    { role: 'arranger', name: hymn.arranger },
    { role: 'harmonizer', name: hymn.harmonizer },
  ]
  return rows.filter((r): r is { role: PersonRole; name: string } => Boolean(r.name))
}

/** Other hymns by the same person, excluding the one being viewed. */
export function otherByPerson(person: PersonPage, exclude: string, limit = 20): HymnRef[] {
  return person.hymns.filter((h) => h.slug !== exclude).slice(0, limit)
}

const ROLE_LABELS: Record<PersonRole, string> = {
  author: 'Text',
  translator: 'Translation',
  composer: 'Tune',
  arranger: 'Arrangement',
  harmonizer: 'Harmonization',
}

/** Musician-facing label for a credit — "Tune", not "composer". */
export const roleLabel = (role: PersonRole): string => ROLE_LABELS[role] ?? role

/** How a person's roles read on their own page: "Text, tune and harmonization". */
export function rolesSentence(roles: readonly PersonRole[]): string {
  const labels = roles.map(roleLabel)
  if (labels.length <= 1) return labels[0] ?? ''
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]!.toLowerCase()}`
}
